import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import { ShowHeader } from "@/common/components/ShowHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useCompany from "@/common/hooks/queries/useCompany";
import { useCompanyEquipments } from "@/common/hooks/queries/useCompanyEquipments";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { EquipmentsTransformer } from "@/common/services/equipmentTransformer";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { maintenanceWorkflowsTableColumns } from "@/features/privateCompanies/constants/maintenance/workflows/maintenanceWorkflowsTableColumns.constant";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";
import { useMaintenanceWorkflows } from "@/features/privateCompanies/hooks/maintenance/workflows/useMaintenanceWorkflows";
import { MaintenanceWorkflowsTransformer } from "@/features/privateCompanies/services/maintenance/workflows/maintenanceWorkflowsTransformer";
import { SearchIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import SelectEquipmentModal from "./components/SelectEquipmentModal";
import { maintenanceWorkflowStatusOptions } from "@/features/privateCompanies/constants/maintenance/workflows/maintenanceWorkflowsStatus.constant";

const CompanyMaintenanceWorkflowsList = () => {
  const { companyId } = useParams();

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);
  const [searchEquipment, setSearchEquipment] = useDebounce('');
  const [searchBranch, setSearchBranch] = useDebounce('');

  const {
    data: company,
    isPending: companyIsPending,
  } = useCompany({
    id: companyId || '',
  });

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    companyId,
    filters: {
      search: searchBranch
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    data: equipmentsOptions,
  } = useCompanyEquipments({
    filters: {
      companyId,
      search: searchEquipment,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EquipmentsTransformer.optionsTransformer({
        icon: false,
      }),
    }),
  });

  const {
    data: workflows,
    meta: workflowsMeta,
    isPending: workflowsIsPending,
    refetch: refetchWorkflows,
    error: workflowsError = {} as any,
  } = useMaintenanceWorkflows({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      companyId,
      status: filterParams.status?.value,
      branchId: filterParams.branch?.value,
      equipmentId: filterParams.equipment?.value,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowsTransformer.tableTransformer(companyId),
    }),
    enabled: !!companyId,
  });

  return (
    <div className="inner-screen overflow-hidden">
      <ShowHeader
        goBack
        goBackProps={{
          fallback: `/companies/${companyId}/maintenance`,
          searchLastRouteFromFallback: true,
        }}
        isLoading={companyIsPending}
        title={company?.general_information?.fantasy_name}
        subtitle="Processos de manutenção (OS)"
        titleClassName="text-md"
        subtitleClassName="text-nowrap font-medium"
        buttons={(
          <SelectEquipmentModal />
        )}
      />

      <ErrorMessageWrapper
        className="flex-1"
        statusCode={workflowsError?.status}
        refetchFunction={refetchWorkflows}
      >
        <div className="flex-1 flex flex-col gap-6 overflow-hidden">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListFilters
                  filters={[
                    {
                      className: '',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                      value: filterParams.status,
                      options: [{ label: 'Todas', value: '' }, ...(maintenanceWorkflowStatusOptions || [])],
                      placeholder: 'Status',
                    },
                    {
                      className: '',
                      onInputChange: setSearchEquipment,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, equipment: e })),
                      value: filterParams.equipment,
                      options: [{ label: 'Todas', value: '' }, ...(equipmentsOptions || [])],
                      placeholder: 'Equipamento',
                    },
                    {
                      className: '',
                      onInputChange: setSearchBranch,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                      value: filterParams.branch,
                      options: [{ label: 'Todas', value: '' }, ...(companyBranchOptions || [])],
                      placeholder: 'Filial',
                    },
                  ]}
                />
              </div>
            </div>
          </div>

          <Table
            className="flex-1 overflow-auto"
            columns={maintenanceWorkflowsTableColumns}
            isLoadingData={workflowsIsPending}
            page={filterParams.page || 1}
            rows={workflows}
            totalPages={workflowsMeta.total_pages}
            totalCount={workflowsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default CompanyMaintenanceWorkflowsList;
